import type { Table as TableType } from "@tanstack/react-table";
import { flexRender } from "@tanstack/react-table";
import { twMerge } from "tailwind-merge";

import type { Styled } from "~/utils";
import { SortIcon } from "./SortIcon";
import { TableBody } from "./TableBody";
import { TableContent } from "./TableContent";
import { TableHead } from "./TableHead";
import { TableHeader } from "./TableHeader";
import { TableRow } from "./TableRow";
import { TableSkeleton } from "./TableSkeleton";
import { TableWrapper } from "./TableWrapper";

export interface TableProps<TData> extends Styled {
  table: TableType<TData>;
  isLoadingData: boolean;
  rowsPerPage: number;
}

/*
   Don't use directly, use SinglePageTable or PaginatedTable instead
*/

export const Table = <TData,>({
  table,
  isLoadingData,
  rowsPerPage,
  className,
}: TableProps<TData>) => {
  return (
    <TableWrapper className={className}>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHead
                key={header.id}
                className={twMerge(
                  header.column.getCanSort() && "cursor-pointer select-none",
                )}
                onClick={header.column.getToggleSortingHandler()}
              >
                <div className="flex items-center gap-2 truncate">
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext(),
                  )}
                  {header.column.getCanSort() && (
                    <SortIcon
                      header={header}
                      className="inline h-3.5 w-3.5 stroke-2"
                    />
                  )}
                </div>
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {isLoadingData ? (
          <TableSkeleton
            numRows={rowsPerPage}
            columnsLength={table.getAllColumns().length}
          />
        ) : (
          <TableContent
            rows={table.getRowModel().rows}
            numColumns={table.getAllColumns().length}
          />
        )}
      </TableBody>
    </TableWrapper>
  );
};
Table.displayName = "Table";
